import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { ShoppingCart, ArrowLeft, Mail, Phone, Clock, CheckCircle, XCircle, Package, CreditCard, Banknote, Image, Eye } from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

const STATUS_OPTIONS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-700" },
  { value: "comprobante_enviado", label: "Comprobante enviado", color: "bg-blue-100 text-blue-700" },
  { value: "pagado", label: "Pagado - Listo", color: "bg-green-100 text-green-700" },
  { value: "entregado", label: "Entregado", color: "bg-violet-100 text-violet-700" },
  { value: "cancelada", label: "Cancelada", color: "bg-red-100 text-red-700" },
  { value: "vencida", label: "Vencida", color: "bg-gray-100 text-gray-700" },
];

export default function AdminLibritosReservasPage() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [comprobanteModal, setComprobanteModal] = useState(false);
  const [selectedComprobante, setSelectedComprobante] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API_URL}/reservations`);
      // Sort by date descending
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setReservations(sorted);
    } catch (error) {
      toast.error("Error al cargar reservas");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reservationId, newStatus) => {
    try {
      await axios.put(`${API_URL}/reservations/${reservationId}/status`, { status: newStatus });
      toast.success("Estado actualizado");
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const confirmPayment = async (reservationId) => {
    try {
      await axios.post(`${API_URL}/admin/reservations/${reservationId}/confirmar-pago`);
      toast.success("Pago confirmado");
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar pago");
    }
  };

  const viewComprobante = (reservation) => {
    setSelectedComprobante(reservation);
    setComprobanteModal(true);
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return <Badge className={option?.color || "bg-gray-100"}>{option?.label || status}</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReservations = filterStatus === "all" 
    ? reservations 
    : reservations.filter(r => r.status === filterStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/libritos">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reservas</h1>
            <p className="text-gray-500">Gestión de reservas de libros</p>
          </div>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Reservas ({filteredReservations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Libro</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Pago</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Vence</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReservations.map((res) => (
                <TableRow key={res.id}>
                  <TableCell className="font-mono text-sm">{res.id}</TableCell>
                  <TableCell className="font-medium">{res.book_title}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{res.customer_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Mail className="w-3 h-3" />
                        {res.customer_email}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Phone className="w-3 h-3" />
                        {res.customer_phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      {res.payment_method === 'transferencia' ? (
                        <><CreditCard className="w-4 h-4 text-blue-500" /> Transferencia</>
                      ) : (
                        <><Banknote className="w-4 h-4 text-green-500" /> Efectivo</>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">${res.book_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</TableCell>
                  <TableCell>{getStatusBadge(res.status)}</TableCell>
                  <TableCell className="text-sm">{formatDate(res.created_at)}</TableCell>
                  <TableCell className="text-sm">
                    {['pendiente', 'comprobante_enviado'].includes(res.status) && (
                      <span className={new Date(res.expires_at) < new Date() ? 'text-red-600' : ''}>
                        {formatDate(res.expires_at)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Ver comprobante si existe */}
                      {res.comprobante_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewComprobante(res)}
                          className="border-blue-300 text-blue-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                      )}
                      
                      {/* Confirmar pago si tiene comprobante enviado */}
                      {res.status === 'comprobante_enviado' && (
                        <Button
                          size="sm"
                          onClick={() => confirmPayment(res.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Confirmar
                        </Button>
                      )}
                      
                      {/* Select para cambiar estado */}
                      <Select
                        value={res.status}
                        onValueChange={(value) => updateStatus(res.id, value)}
                        disabled={res.status === 'vencida' || res.status === 'cancelada'}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredReservations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    No hay reservas
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal para ver comprobante */}
      <Dialog open={comprobanteModal} onOpenChange={setComprobanteModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Comprobante de Pago
            </DialogTitle>
          </DialogHeader>
          {selectedComprobante && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 text-sm grid grid-cols-2 gap-2">
                <p><strong>Reserva:</strong> {selectedComprobante.id}</p>
                <p><strong>Libro:</strong> {selectedComprobante.book_title}</p>
                <p><strong>Cliente:</strong> {selectedComprobante.customer_name}</p>
                <p><strong>Precio:</strong> ${selectedComprobante.book_price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</p>
              </div>
              {selectedComprobante.comprobante_url && (
                <div className="border rounded-lg overflow-hidden">
                  <img 
                    src={selectedComprobante.comprobante_url} 
                    alt="Comprobante" 
                    className="w-full max-h-96 object-contain"
                  />
                </div>
              )}
              {selectedComprobante.status === 'comprobante_enviado' && (
                <Button
                  onClick={() => {
                    confirmPayment(selectedComprobante.id);
                    setComprobanteModal(false);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Pago
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
